import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';
import { CustomFieldDefinition } from '../state/active-board-context';

export interface ActiveFilters {
  readonly labels: string[];
  readonly members: string[];
  readonly dueDateFilter: 'overdue' | 'no-due' | null;
  readonly customFieldFilters: Record<string, string[] | boolean | null>;
}

export interface FilterBarProps {
  readonly labels: { labelId: string; name: string }[];
  readonly members: { userId: string; displayName: string }[];
  readonly activeFilters: ActiveFilters;
  readonly onFilterChange: (filters: ActiveFilters) => void;
  /** The authenticated user's ID — used to show "Me" option in assignee filter */
  readonly currentUserId?: string;
  /** Custom field definitions to render dynamic filter controls for dropdown and boolean types */
  readonly customFieldDefinitions?: readonly CustomFieldDefinition[];
}

// ---------------------------------------------------------------------------
// Multi-select dropdown (portal-based, consistent styling)
// ---------------------------------------------------------------------------

interface MultiSelectDropdownProps {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly options: { value: string; label: string }[];
  readonly selected: string[];
  readonly onChange: (selected: string[]) => void;
}

function MultiSelectDropdown({ id, label, placeholder, options, selected, onChange }: MultiSelectDropdownProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, []);

  const handleToggle = useCallback(() => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen((v) => !v);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const toggleValue = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  };

  const isActive = selected.length > 0;
  const displayText = isActive ? `${placeholder} (${selected.length})` : `${placeholder}…`;

  return (
    <div className="relative inline-flex items-center shrink-0">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={handleToggle}
        className={cn(
          'flex h-8 w-[160px] items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          isActive && 'border-primary ring-1 ring-primary/30 bg-primary/5',
        )}
      >
        <span className="truncate">{displayText}</span>
        <svg className="h-3 w-3 opacity-50 shrink-0 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>

      {isActive && (
        <Badge
          className="absolute -right-1 -top-1 h-4 w-4 items-center justify-center rounded-full p-0 text-[10px]"
          aria-label={`${selected.length} selected`}
        >
          {selected.length}
        </Badge>
      )}

      {open && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          aria-label={label}
          aria-multiselectable="true"
          className="fixed z-[9999] min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
          style={{ top: menuPos.top, left: menuPos.left, width: Math.max(menuPos.width, 160) }}
        >
          <div className="p-1 max-h-60 overflow-y-auto">
            {options.map((opt) => {
              const isSelected = selected.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => toggleValue(opt.value)}
                  className="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {isSelected && <span className="text-primary font-bold">✓</span>}
                  </span>
                  <span className="truncate">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single-select dropdown (for due date — same portal-based pattern)
// ---------------------------------------------------------------------------

interface SingleSelectDropdownProps {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly options: { value: string; label: string }[];
  readonly selected: string | null;
  readonly onChange: (value: string | null) => void;
}

function SingleSelectDropdown({ id, label, placeholder, options, selected, onChange }: SingleSelectDropdownProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, []);

  const handleToggle = useCallback(() => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen((v) => !v);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const isActive = selected !== null;
  const selectedOption = options.find((o) => o.value === selected);
  const displayText = selectedOption?.label ?? `${placeholder}…`;

  return (
    <div className="relative inline-flex items-center shrink-0">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={handleToggle}
        className={cn(
          'flex h-8 w-[160px] items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          isActive && 'border-primary ring-1 ring-primary/30 bg-primary/5',
        )}
      >
        <span className="truncate">{displayText}</span>
        <svg className="h-3 w-3 opacity-50 shrink-0 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>

      {isActive && (
        <Badge
          className="absolute -right-1 -top-1 h-4 w-4 items-center justify-center rounded-full p-0 text-[10px]"
          aria-label="Filter active"
        >
          ✓
        </Badge>
      )}

      {open && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          aria-label={label}
          className="fixed z-[9999] min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
          style={{ top: menuPos.top, left: menuPos.left, width: Math.max(menuPos.width, 160) }}
        >
          <div className="p-1">
            {/* Clear option */}
            <button
              type="button"
              role="option"
              aria-selected={!isActive}
              onClick={() => { onChange(null); setOpen(false); }}
              className="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
            >
              <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                {!isActive && <span className="text-primary font-bold">✓</span>}
              </span>
              <span className="text-muted-foreground">Clear</span>
            </button>
            {options.map((opt) => {
              const isSelected = selected === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  className="relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {isSelected && <span className="text-primary font-bold">✓</span>}
                  </span>
                  <span className="truncate">{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FilterBar — unified scrollable row with Labels, Due Date, Assignee, Clear all
// ---------------------------------------------------------------------------

export function FilterBar({ labels, members, activeFilters, onFilterChange, currentUserId, customFieldDefinitions }: FilterBarProps): React.ReactElement {
  const customFieldFilterCount = countCustomFieldFilters(activeFilters.customFieldFilters);
  const activeFilterCount =
    activeFilters.labels.length +
    activeFilters.members.length +
    (activeFilters.dueDateFilter ? 1 : 0) +
    customFieldFilterCount;

  const handleLabelChange = (selected: string[]) => {
    onFilterChange({ ...activeFilters, labels: selected });
  };

  const handleDueDateChange = (value: string | null) => {
    onFilterChange({ ...activeFilters, dueDateFilter: value as 'overdue' | 'no-due' | null });
  };

  const handleMemberChange = (selected: string[]) => {
    onFilterChange({ ...activeFilters, members: selected });
  };

  const handleCustomFieldDropdownChange = (fieldId: string, selected: string[]) => {
    const updated = { ...activeFilters.customFieldFilters };
    if (selected.length === 0) {
      delete updated[fieldId];
    } else {
      updated[fieldId] = selected;
    }
    onFilterChange({ ...activeFilters, customFieldFilters: updated });
  };

  const handleCustomFieldBooleanChange = (fieldId: string, value: string | null) => {
    const updated = { ...activeFilters.customFieldFilters };
    if (value === 'true') {
      updated[fieldId] = true;
    } else if (value === 'false') {
      updated[fieldId] = false;
    } else {
      delete updated[fieldId];
    }
    onFilterChange({ ...activeFilters, customFieldFilters: updated });
  };

  const handleClear = () => {
    onFilterChange({ labels: [], members: [], dueDateFilter: null, customFieldFilters: {} });
  };

  const labelOptions = labels.map((l) => ({ value: l.labelId, label: l.name }));
  const dueDateOptions = [
    { value: 'overdue', label: 'Overdue' },
    { value: 'no-due', label: 'No due date' },
  ];

  // Build assignee options: "Me" first (if currentUserId is provided), then other members
  const memberOptions = buildMemberOptions(members, currentUserId);

  // Build custom field filter controls (only dropdown and boolean types)
  const filterableDefinitions = (customFieldDefinitions ?? []).filter(
    (d) => d.fieldType === 'dropdown-single' || d.fieldType === 'dropdown-multi' || d.fieldType === 'boolean',
  );

  const booleanFilterOptions = [
    { value: 'true', label: 'Checked' },
    { value: 'false', label: 'Unchecked' },
  ];

  return (
    <fieldset
      role="group"
      aria-label="Board filters"
      className="flex items-center gap-2 overflow-x-auto flex-nowrap"
    >
      <MultiSelectDropdown
        id="filter-label"
        label="Filter by label"
        placeholder="Labels"
        options={labelOptions}
        selected={activeFilters.labels}
        onChange={handleLabelChange}
      />

      <SingleSelectDropdown
        id="filter-due"
        label="Filter by due date"
        placeholder="Due date"
        options={dueDateOptions}
        selected={activeFilters.dueDateFilter}
        onChange={handleDueDateChange}
      />

      <MultiSelectDropdown
        id="filter-assignee"
        label="Filter by assignee"
        placeholder="Assignee"
        options={memberOptions}
        selected={activeFilters.members}
        onChange={handleMemberChange}
      />

      {filterableDefinitions.map((def) => {
        if (def.fieldType === 'boolean') {
          const currentValue = activeFilters.customFieldFilters[def.fieldId];
          const selectedValue = currentValue === true ? 'true' : currentValue === false ? 'false' : null;
          return (
            <SingleSelectDropdown
              key={def.fieldId}
              id={`filter-cf-${def.fieldId}`}
              label={`Filter by ${def.name}`}
              placeholder={def.name}
              options={booleanFilterOptions}
              selected={selectedValue}
              onChange={(v) => handleCustomFieldBooleanChange(def.fieldId, v)}
            />
          );
        }
        // dropdown-single or dropdown-multi — render as multi-select filter
        const dropdownOptions = def.options.map((opt) => ({ value: opt, label: opt }));
        const currentSelected = activeFilters.customFieldFilters[def.fieldId];
        const selectedValues = Array.isArray(currentSelected) ? currentSelected : [];
        return (
          <MultiSelectDropdown
            key={def.fieldId}
            id={`filter-cf-${def.fieldId}`}
            label={`Filter by ${def.name}`}
            placeholder={def.name}
            options={dropdownOptions}
            selected={selectedValues}
            onChange={(s) => handleCustomFieldDropdownChange(def.fieldId, s)}
          />
        );
      })}

      {activeFilterCount > 0 && (
        <button
          type="button"
          onClick={handleClear}
          aria-label={`Clear all filters (${activeFilterCount} active)`}
          className="shrink-0 ml-auto text-xs text-muted-foreground hover:text-foreground hover:underline flex items-center gap-1 whitespace-nowrap"
        >
          Clear all ({activeFilterCount})
        </button>
      )}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildMemberOptions(
  members: { userId: string; displayName: string }[],
  currentUserId?: string,
): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];

  if (currentUserId) {
    const me = members.find((m) => m.userId === currentUserId);
    if (me) {
      options.push({ value: me.userId, label: `Me (${me.displayName})` });
    }
  }

  for (const member of members) {
    if (member.userId === currentUserId) continue;
    options.push({ value: member.userId, label: member.displayName });
  }

  return options;
}

function countCustomFieldFilters(customFieldFilters: Record<string, string[] | boolean | null>): number {
  let count = 0;
  for (const value of Object.values(customFieldFilters)) {
    if (value === null) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    count++;
  }
  return count;
}
